// @ts-strict-ignore
import type * as React from 'react';
import { Box, styled } from '@mui/material';
import type { SxProps } from '@mui/material';
import { Link } from 'react-router';
import { ArrowLeftIcon } from '../icons';
import type { NamedColors } from '../../theme';

const Root = styled(Box, {
    shouldForwardProp: (prop) =>
        !['disabled', 'variant', 'color'].includes(prop as string),
})<Pick<BackLinkProps, 'disabled' | 'variant' | 'color'>>(
    ({ theme, disabled, variant, color }) => ({
        '&>*': {
            color: theme.bino.color[color],
        },
        ...(disabled
            ? {
                  opacity: 0.5,
                  cursor: 'not-allowed',
              }
            : {
                  '&:hover *': {
                      color: theme.palette.primary.main,
                      cursor: 'pointer',
                  },
              }),
        ...{
            default: {
                display: 'flex',
                alignItems: 'center',
                padding: '0 24px 0 20px',
                height: 54,
                borderBottom: `solid 1px ${theme.bino.color.boxShadowBorder}`,
            },
            inline: {
                display: 'inline',
            },
            'full-screen-back-link': {
                padding: '8px 16px',
                display: 'inline-flex',
                alignItems: 'center',
                fontWeight: 'bold',
            },
        }[variant],
    }),
);

const StyledLink = styled(Link)<{
    color: NamedColors;
}>(({ theme, color, onClick }) => ({
    textDecoration: 'none',
    textTransform: 'uppercase',
    fontSize: 12,
    color: theme.bino.color[color],
    paddingLeft: 16,
    paddingTop: 2,
    display: 'inline-block',
    letterSpacing: 0.6,
    cursor: onClick ? 'pointer' : undefined,
    [theme.breakpoints.down('md')]: {
        link: {
            textDecoration: 'none',
            textTransform: 'uppercase',
            fontSize: 11,
            padding: 12,
        },
    },
}));

type BackLinkVariant = 'default' | 'inline' | 'full-screen-back-link';
type BackLinkProps = {
    color?: NamedColors;
    to?: string;
    onClick?(): void;
    disabled?: boolean;
    variant?: BackLinkVariant;
    children: React.ReactNode;
    sx?: SxProps;
};

const BackLink: React.FunctionComponent<BackLinkProps> = ({
    color = 'gray',
    disabled = false,
    to,
    onClick,
    variant = 'default',
    children,
    sx,
}) => {
    return (
        <Root disabled={disabled} variant={variant} sx={sx} color={color}>
            <ArrowLeftIcon data-testid="left-icon" size={12} />
            <StyledLink
                color={color}
                as={disabled || !to ? 'span' : undefined}
                to={to}
                onClick={onClick}
            >
                {children}
            </StyledLink>
        </Root>
    );
};

export default BackLink;
